import { useCallback, useEffect, useRef, useState } from 'react';
import {
  applyRowPatch,
  listDayTasks,
  updateTask,
  type RowPatch,
  type Task,
  type TaskSummary,
} from '../../lib/tasks';
import { UNFINISHED_STATUSES, statusChangedMessage } from '../../lib/taskStatus';
import { useToast } from '../toast';
import { useRowFlash } from '../tasks/useRowFlash';
import { shiftDateOnly, todayInputValue } from '../../lib/taskDates';
import { onSeriesChanged } from '../../lib/recurrence';

/**
 * The day's work, across every project -- the data half of DayTasksCard.
 *
 * Lifted out of the card for the reason ProjectTasks lifts its own list into
 * useTaskListState: the task panel now opens *over* this page, and an edit made
 * in the panel has to reach the row sitting behind it. The card renders the
 * rows and the page owns them, so the panel and the list have one copy between
 * them rather than two that drift apart until somebody reloads.
 *
 * Both windows include overdue work, because the question the card answers is
 * "what needs doing", and something a week late does not stop needing doing
 * when you look at tomorrow. So the window is only ever an upper bound, and
 * "tomorrow" widens it rather than moving it.
 *
 * "Day" rather than "Due" throughout. The card was called Due while a due date
 * was the only way onto it; a start date that has arrived is the other way now,
 * so the old name described half the question. A hook named for a filter it no
 * longer applies alone is the kind of name that quietly teaches the wrong rule
 * -- `qualifies` below is the rule.
 */
export type DayWindow = 'today' | 'tomorrow';

/** The latest date this window admits, in the reader's own timezone. Read
 *  fresh on every use rather than memoised: a dashboard left open overnight
 *  would otherwise keep answering for yesterday. */
function dayBoundFor(dayWindow: DayWindow): string {
  const today = todayInputValue();
  return dayWindow === 'today' ? today : shiftDateOnly(today, 1);
}

/**
 * Whether a task is still an answer to the question this card asks -- the same
 * test the server's query applies, restated here so an edited row can be judged
 * without a refetch.
 *
 * Two ways to qualify, matching the two bounds the fetch sends. A task is on
 * the day if it is *due* by the window's end, or if it has *started* by then --
 * a start date that has arrived is a task you are meant to be working on,
 * whether or not it carries a deadline, and the card that opens the morning is
 * where it should be. A date pushed out only takes the row off the card when
 * the other date has not got it there anyway.
 *
 * Read by both the panel's write-back and the row's own edits, because a task
 * stops belonging on this list for the same reasons either way: it is finished,
 * or neither of its dates has arrived.
 */
function qualifies(
  task: Pick<TaskSummary, 'status' | 'dueDate' | 'startDate'>,
  dayWindow: DayWindow,
): boolean {
  if (task.status === 'DONE') return false;
  const bound = dayBoundFor(dayWindow);
  // Lexicographic comparison is chronologically correct for YYYY-MM-DD, which
  // is why these never go through `new Date` -- see lib/taskDates.ts.
  return (
    (task.dueDate !== null && task.dueDate <= bound) ||
    (task.startDate !== null && task.startDate <= bound)
  );
}

export function useDayTasks({ onTaskChanged }: { onTaskChanged?: (task: Task) => void } = {}) {
  const [dayWindow, setDayWindow] = useState<DayWindow>('today');
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  /**
   * Two error slots, not one.
   *
   * A failed *load* has no list to show, so it stands in for one. A failed
   * *edit* still has the list -- and the row you were acting on is in it -- so
   * replacing the card with a red sentence takes away the thing you were
   * looking at. The tasks page already separates these two; this is the same
   * split.
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /** One counter per task, bumped on every edit, so a slow reply to an earlier
   *  click cannot overwrite a later one -- the same guard the tasks page keeps
   *  in useTaskListState, which matters more here now that a row can be clicked
   *  twice in a row to walk it up the statuses. */
  const patchStamps = useRef(new Map<string, number>());
  /** The rows as they stand, so the callbacks below can read them without being
   *  rebuilt -- and handed to the panel -- every time one changes. */
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  /** Read through a ref for the same reason: `patch` is built once, and the
   *  page's callback is rebuilt whenever the other list changes. */
  const changedRef = useRef(onTaskChanged);
  changedRef.current = onTaskChanged;
  /** And the window, for that same "built once": `patch` has to know which
   *  bound an edited date is being judged against, and rebuilding it whenever
   *  the segmented control moves would rebuild the panel's props with it. */
  const windowRef = useRef(dayWindow);
  windowRef.current = dayWindow;
  /** Also for `patch`, which raises the Undo toast. */
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  /** The row a date edit just moved. `flash` is stable, so `patch` can still
   *  be built once. */
  const { flashId, flash } = useRowFlash();

  /** Bumped when a series gains or loses an instance, to refetch the same
   *  window -- quietly, because the card already has rows on it. */
  const [seriesToken, setSeriesToken] = useState(0);
  const quietReload = useRef(false);
  useEffect(
    () =>
      onSeriesChanged(() => {
        quietReload.current = true;
        setSeriesToken((token) => token + 1);
      }),
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    // A refetch for a spawned instance keeps the rows up while it lands; a
    // change of window is a different list, and gets the skeleton.
    if (!quietReload.current) setLoading(true);
    quietReload.current = false;

    // One bound, sent as both: "due by the end of this window, or started by
    // then". `qualifies` below judges an edited row against the same two.
    const bound = dayBoundFor(dayWindow);

    listDayTasks(
      { dueTo: bound, startedBy: bound, status: UNFINISHED_STATUSES },
      controller.signal,
    )
      .then((data) => {
        setTasks(data.tasks);
        setTotal(data.total);
        setLoadError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(
          requestError instanceof Error
            ? requestError.message
            : "Could not load today's tasks.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [dayWindow, seriesToken]);

  /**
   * Take a row out, and the count with it.
   *
   * `total` is the server's count for this query, and a task that no longer
   * belongs here is one the server would no longer return either -- so leaving
   * the count alone would have the card claim it is truncated because something
   * was finished. Guarded on the row actually being present, so a second
   * removal of something already gone is not a second decrement.
   */
  const removeTask = useCallback((taskId: string) => {
    if (!tasksRef.current.some((entry) => entry.id === taskId)) return;
    setTasks((current) => current.filter((entry) => entry.id !== taskId));
    setTotal((current) => Math.max(0, current - 1));
  }, []);

  /**
   * A task the panel changed, folded back into the list behind it.
   *
   * Updates in place or drops, and never inserts: the panel can be opened on a
   * task with nothing to do with today -- a deep link, or a prerequisite
   * followed from another task -- and a card headed "Today's Tasks" quietly
   * growing a row for one would be a lie about what is on it. A task that stops
   * qualifying leaves, which is what the next fetch would do anyway.
   */
  const syncTask = useCallback(
    (task: Task) => {
      if (!tasksRef.current.some((entry) => entry.id === task.id)) return;

      if (!qualifies(task, dayWindow)) {
        removeTask(task.id);
        return;
      }

      setTasks((current) => current.map((entry) => (entry.id === task.id ? task : entry)));
    },
    [dayWindow, removeTask],
  );

  /**
   * One inline row edit -- a status or a due date -- applied optimistically.
   *
   * The row is already on screen and a round trip would make a click feel
   * broken, so the change lands first and the request follows; a failure puts
   * the row back where it was and says what the server said.
   *
   * An edit either keeps the row or takes it off the card, and `qualifies`
   * decides which. Completing a task is the obvious case, and pushing its due
   * date past the window is the same event: something you have just moved out
   * of the day is not on it. Both are what a refetch would do anyway. Anything
   * else keeps the row and only redraws the cell you changed, so starting a
   * task, or pulling one forward to today, leaves it where you can still see
   * it. A task already on the card for its start date keeps its row through a
   * due-date change, because the date that put it there has not moved.
   *
   * A row that stays and gets a new due date moves, because the card orders
   * by due date when it renders (DayTasksCard). It used to sit out of order
   * until the next fetch, so as not to slide away from the pointer that moved
   * it; it flashes where it lands instead, which is the same answer the Tasks
   * pages give.
   */
  const patch = useCallback(
    async (
      task: TaskSummary,
      changes: RowPatch,
      {
        undoable = true,
        at,
      }: {
        /** False for the Undo itself, which must not offer to undo itself. */
        undoable?: boolean;
        /** Where a row that is not on the card goes if the edit puts it back
         *  on -- which only an Undo does, so it is the slot the row left. */
        at?: number;
      } = {},
    ) => {
      const sent = (patchStamps.current.get(task.id) ?? 0) + 1;
      patchStamps.current.set(task.id, sent);

      // Captured before the removal, so a failed edit can put the row back where
      // it was rather than at the end of a list ordered by due date.
      const index = tasksRef.current.findIndex((item) => item.id === task.id);
      const present = index !== -1;
      // Judged on the patched task rather than on the change: "does this still
      // belong here" is a question about the whole row -- both its dates, against
      // whichever window is showing.
      const patched = applyRowPatch(task, changes);
      const belongs = qualifies(patched, windowRef.current);
      const leaves = present && !belongs;
      const arrives = !present && belongs;

      setTasks((current) =>
        leaves
          ? current.filter((item) => item.id !== task.id)
          : arrives
            ? [...current.slice(0, at ?? current.length), patched, ...current.slice(at ?? current.length)]
            : current.map((item) => (item.id === task.id ? patched : item)),
      );
      if (leaves) setTotal((current) => Math.max(0, current - 1));
      if (arrives) setTotal((current) => current + 1);
      setActionError(null);
      if (belongs && changes.dueDate !== undefined && changes.dueDate !== task.dueDate) {
        flash(task.id);
      }

      try {
        const { task: updated, spawned } = await updateTask(task.id, changes);
        if (patchStamps.current.get(task.id) !== sent) return;
        if (!leaves) {
          setTasks((current) => current.map((item) => (item.id === task.id ? updated : item)));
        }
        // The focus card may be holding this same task -- see the note on the
        // dashboard's `crossSync`. On the reply rather than optimistically,
        // because that is the first point there is a whole `Task` to hand over.
        changedRef.current?.(updated);

        // On the reply, not the click: a change that failed has already been
        // put back, and offering to undo it would be a lie.
        if (undoable && changes.status !== undefined && changes.status !== task.status) {
          const from = changes.status;
          const to = task.status;
          toastRef.current(statusChangedMessage(task.title, from, spawned), {
            action: {
              label: 'Undo',
              onAction: () => {
                // Only if nothing has moved it since -- that would be the newer
                // word. Off the card, the reply is the last copy there is.
                const current = tasksRef.current.find((item) => item.id === task.id) ?? updated;
                if (current.status !== from) return;
                void patchRef.current(current, { status: to }, {
                  undoable: false,
                  at: present ? index : undefined,
                });
              },
            },
          });
        }
      } catch (updateError: unknown) {
        if (patchStamps.current.get(task.id) !== sent) return;
        setTasks((current) =>
          leaves
            ? [...current.slice(0, index), task, ...current.slice(index)]
            : arrives
              ? current.filter((item) => item.id !== task.id)
              : current.map((item) => (item.id === task.id ? task : item)),
        );
        if (leaves) setTotal((current) => current + 1);
        if (arrives) setTotal((current) => Math.max(0, current - 1));
        setActionError(
          updateError instanceof Error ? updateError.message : 'Could not update this task.',
        );
      }
    },
    [flash],
  );
  // The Undo calls back into `patch`, which is built once and cannot name
  // itself in its own initializer's dependencies.
  const patchRef = useRef(patch);
  patchRef.current = patch;

  return {
    dayWindow,
    setDayWindow,
    tasks,
    total,
    loading,
    loadError,
    actionError,
    setActionError,
    patch,
    syncTask,
    removeTask,
    flashId,
  };
}

/** What the card renders and what the panel writes back through, named once so
 *  neither has to restate the hook's shape. */
export type DayTasks = ReturnType<typeof useDayTasks>;
